import { grayPalette, primaryPalette, tempusPalette, fullPalette } from '@tcl-argon-colors/colors';
import { ButtonColors } from '@tcl-argon-foundations/base-button/lib/BaseButton';
import { InputTheme } from '@tcl-argon-models/models';

type PartialColors = Partial<{
  activeColors: Partial<ButtonColors>;
  defaultColors: Partial<ButtonColors>;
  hoverColors: Partial<ButtonColors>;
}>;

type Colors = {
  activeColors: ButtonColors;
  defaultColors: ButtonColors;
  hoverColors: ButtonColors;
};

const errorTheme: PartialColors = {
  defaultColors: {
    color: 'white',
    backgroundColor: primaryPalette.errorPrimary,
  },
  hoverColors: {
    backgroundColor: fullPalette.radarPrimary,
  },
  activeColors: {
    backgroundColor: fullPalette.radar90,
  },
};

const successTheme: PartialColors = {
  defaultColors: {
    color: 'white',
    backgroundColor: primaryPalette.successPrimary,
  },
  hoverColors: {
    backgroundColor: fullPalette.opticPrimary,
  },
  activeColors: {
    backgroundColor: fullPalette.optic90,
  },
};

const warningTheme: PartialColors = {
  defaultColors: {
    color: 'white',
    backgroundColor: primaryPalette.warningPrimary,
  },
  hoverColors: {
    backgroundColor: fullPalette.infrared60,
  },
  activeColors: {
    backgroundColor: fullPalette.infrared80,
  },
};

const lemonTheme: PartialColors = {
  defaultColors: {
    color: 'white',
    backgroundColor: primaryPalette.lemonPrimary,
  },
  hoverColors: {
    backgroundColor: fullPalette.edison60,
  },
  activeColors: {
    backgroundColor: fullPalette.edison80,
  },
};

const tempusTheme: PartialColors = {
  defaultColors: {
    color: 'white',
    backgroundColor: primaryPalette.tempusPrimary,
  },
  hoverColors: {
    backgroundColor: tempusPalette.tempus60,
  },
  activeColors: {
    backgroundColor: tempusPalette.tempus55,
  },
};

const defaultTheme: PartialColors = {
  defaultColors: {
    color: grayPalette.gray100,
    backgroundColor: grayPalette.gray45,
  },
  hoverColors: {
    backgroundColor: grayPalette.gray30,
  },
  activeColors: {
    backgroundColor: grayPalette.gray50,
  },
};

const darkTheme: PartialColors = {
  defaultColors: {
    color: 'white',
    backgroundColor: grayPalette.gray70,
  },
  hoverColors: {
    backgroundColor: grayPalette.gray60,
  },
  activeColors: {
    backgroundColor: grayPalette.gray80,
  },
};

const themeToThemeStyles = (theme: InputTheme) => {
  if (theme === InputTheme.Error) {
    return { ...errorTheme };
  } else if (theme === InputTheme.Tempus) {
    return { ...tempusTheme };
  } else if (theme === InputTheme.Success) {
    return { ...successTheme };
  } else if (theme === InputTheme.Lemon) {
    return { ...lemonTheme };
  } else if (theme === InputTheme.Warning) {
    return { ...warningTheme };
  } else if (theme === InputTheme.Dark) {
    return { ...darkTheme };
  }

  return { ...defaultTheme };
};

export const getButtonColorsForTheme = (theme: InputTheme): Colors => {
  const styles: PartialColors = themeToThemeStyles(theme);

  styles.defaultColors.borderColor = styles.defaultColors.backgroundColor;

  styles.hoverColors.color = styles.defaultColors.color;
  styles.hoverColors.borderColor = styles.hoverColors.backgroundColor;

  styles.activeColors.color = styles.defaultColors.color;
  styles.activeColors.borderColor = styles.activeColors.backgroundColor;

  return styles as Colors;
};

export const getFlipButtonColorsForTheme = (theme: InputTheme): Colors => {
  const {
    defaultColors: { color, backgroundColor },
  } = themeToThemeStyles(theme);

  return {
    defaultColors: {
      color: backgroundColor,
      backgroundColor: color,
      borderColor: backgroundColor,
    },
    hoverColors: {
      color,
      backgroundColor,
      borderColor: backgroundColor,
    },
    activeColors: {
      color,
      backgroundColor,
      borderColor: backgroundColor,
    },
  };
};
